import { Component, computed, inject, input, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { LateFeeOccurrenceType, LateFeeTierUiModel } from '../../../../core/models/ui/late-fee-config.model';
import { DateFormat, DateUtilities } from '../../../../core/utilities/date.utilities';
import { CurrencyUtilities } from '../../../../core/utilities/currency.utilities';
import { UserSessionService } from '../../../../core/services/user-session.service';

interface TierRow {
    rowIndex: number;
    colorIndex: number;
    amount: number;
    gracePeriodDays: number;
    occurrenceType: LateFeeOccurrenceType;
    frequencyDays?: number;
    multipleCount?: number;

    graceStartDay: number;
    graceEndDay: number;
    accrualStartDay: number;
    accrualEndDay: number;
    fadeEndDay?: number;
    indefinite: boolean;
    tickDays: number[];
    accrualCount?: number;
    tierTotal?: number;
    startDate?: Date;
    endDate?: Date;
}

export interface TimelineMarker {
    rowIndex: number;
    day: number;
    tierNumber: number;
    accrualNumber: number;
    accrualsInTier?: number;
    amount: number;
    cumulativeTotal: number;
    indefiniteTier: boolean;
    colorIndex: number;
    date?: Date;
}

const VIEWBOX_WIDTH = 1000;
const VIEWBOX_HEIGHT = 60;
const BAND_TOP = 16;
const BAND_HEIGHT = 28;
const INDEFINITE_VISUAL_TAIL_DAYS = 30;
const MAX_TICKS_PER_TIER = 60;
const ZERO_WIDTH_BAND_DAYS = 0.4;

@Component({
    selector: 'ms-late-fee-timeline',
    standalone: true,
    imports: [CommonModule, FontAwesomeModule],
    templateUrl: './late-fee-timeline.component.html',
    styleUrl: './late-fee-timeline.component.scss'
})
export class LateFeeTimelineComponent {

    public dueDate = input<string | undefined>(undefined);
    public tiers = input<LateFeeTierUiModel[]>([]);

    public readonly viewBoxWidth = VIEWBOX_WIDTH;
    public readonly viewBoxHeight = VIEWBOX_HEIGHT;
    public readonly bandTop = BAND_TOP;
    public readonly bandHeight = BAND_HEIGHT;

    private userSessionService = inject(UserSessionService);

    public dueDateObj = computed<Date | undefined>(() => {
        const raw = this.dueDate();
        if (!raw) return undefined;
        try {
            return DateUtilities.parseLocalDate(raw);
        } catch {
            return undefined;
        }
    });

    public tierRows = computed<TierRow[]>(() => {
        const tiers = this.tiers();
        const dueDate = this.dueDateObj();
        return tiers.map((tier, index) => this.buildRow(tier, index, dueDate));
    });

    public hasAnyTiers = computed<boolean>(() => this.tiers().length > 0);

    public hasIndefiniteTier = computed<boolean>(() =>
        this.tierRows().some(r => r.indefinite)
    );

    public totalVisibleDays = computed<number>(() => {
        const rows = this.tierRows();
        if (rows.length === 0) return 1;
        let max = 0;
        for (const row of rows) {
            const end = row.fadeEndDay ?? row.accrualEndDay;
            if (end > max) max = end;
        }
        const padded = Math.max(max + 1, Math.ceil(max * 1.02));
        return Math.max(2, padded);
    });

    public dayTicks = computed<number[]>(() => {
        const total = Math.floor(this.totalVisibleDays());
        const ticks: number[] = [];
        for (let d = 0; d <= total; d++) {
            ticks.push(d);
        }
        return ticks;
    });

    public majorTickInterval = computed<number>(() => {
        const total = this.totalVisibleDays();
        if (total <= 10) return 1;
        if (total <= 25) return 5;
        if (total <= 60) return 10;
        return 20;
    });

    public isMajorTick(day: number): boolean {
        return day % this.majorTickInterval() === 0;
    }

    public markers = computed<TimelineMarker[]>(() => {
        const dueDate = this.dueDateObj();
        const result: TimelineMarker[] = [];

        for (const row of this.tierRows()) {
            let runningTotal = 0;
            this.effectiveTicks(row).forEach((day, i) => {
                runningTotal += row.amount;
                result.push({
                    rowIndex: row.rowIndex,
                    day,
                    tierNumber: row.rowIndex + 1,
                    accrualNumber: i + 1,
                    accrualsInTier: row.accrualCount,
                    amount: row.amount,
                    cumulativeTotal: runningTotal,
                    indefiniteTier: row.indefinite,
                    colorIndex: row.colorIndex,
                    date: dueDate ? DateUtilities.addDays(dueDate, day) : undefined,
                });
            });
        }

        return result;
    });

    private effectiveTicks(row: TierRow): number[] {
        if (!row.indefinite || !row.frequencyDays || row.frequencyDays <= 0) {
            return row.tickDays;
        }
        const total = this.totalVisibleDays();
        const result = [...row.tickDays];
        const freq = row.frequencyDays;
        const lastTick = result.length > 0 ? result[result.length - 1] : row.accrualStartDay - freq;
        let nextTick = lastTick + freq;
        while (nextTick <= total && result.length < MAX_TICKS_PER_TIER) {
            result.push(nextTick);
            nextTick += freq;
        }
        return result;
    }

    public definiteTotal = computed<number>(() => {
        return this.tierRows()
            .filter(r => !r.indefinite)
            .reduce((sum, r) => sum + (r.tierTotal ?? 0), 0);
    });

    public hoveredMarker = signal<TimelineMarker | null>(null);
    public hoveredDueBarRow = signal<number | null>(null);
    public hoveredScaleDay = signal<number | null>(null);

    public onScaleMouseMove(event: MouseEvent): void {
        const target = event.currentTarget as HTMLElement;
        const rect = target.getBoundingClientRect();
        if (rect.width <= 0) return;
        const relativeX = event.clientX - rect.left;
        if (relativeX < 0 || relativeX > rect.width) {
            this.hoveredScaleDay.set(null);
            return;
        }
        const total = this.totalVisibleDays();
        const day = Math.max(0, Math.min(total, Math.round((relativeX / rect.width) * total)));
        this.hoveredScaleDay.set(day);
    }

    public onScaleMouseLeave(): void {
        this.hoveredScaleDay.set(null);
    }

    public scrubLeftPercent(): number {
        const day = this.hoveredScaleDay();
        if (day === null) return 0;
        const total = this.totalVisibleDays();
        return total > 0 ? (day / total) * 100 : 0;
    }

    public scrubLeftFraction(): number {
        const day = this.hoveredScaleDay();
        if (day === null) return 0;
        const total = this.totalVisibleDays();
        return total > 0 ? day / total : 0;
    }

    public scrubDate(day: number): Date | undefined {
        const due = this.dueDateObj();
        if (!due) return undefined;
        return DateUtilities.addDays(due, day);
    }

    public scrubCumulativeFees(day: number): { totalFees: number; perTier: { tierNumber: number; fees: number; count: number; colorIndex: number; }[]; } {
        const perTier: { tierNumber: number; fees: number; count: number; colorIndex: number; }[] = [];
        let totalFees = 0;
        for (const row of this.tierRows()) {
            let count = 0;
            for (const t of this.effectiveTicks(row)) {
                if (t <= day) count++;
            }
            const fees = count * row.amount;
            totalFees += fees;
            if (count > 0) {
                perTier.push({
                    tierNumber: row.rowIndex + 1,
                    fees,
                    count,
                    colorIndex: row.colorIndex,
                });
            }
        }
        return { totalFees, perTier };
    }

    public onDueBarEnter(rowIndex: number): void {
        this.hoveredDueBarRow.set(rowIndex);
    }

    public onDueBarLeave(): void {
        this.hoveredDueBarRow.set(null);
    }

    public isDueBarHovered(rowIndex: number): boolean {
        return this.hoveredDueBarRow() === rowIndex;
    }

    public dueBarTooltipXPercent(): number {
        const total = this.totalVisibleDays();
        if (total <= 0) return 0;
        return (0.5 / total) * 100;
    }

    public onMarkerEnter(marker: TimelineMarker): void {
        this.hoveredMarker.set(marker);
    }

    public onMarkerLeave(): void {
        this.hoveredMarker.set(null);
    }

    public isMarkerActive(marker: TimelineMarker): boolean {
        const hovered = this.hoveredMarker();
        if (!hovered) return false;
        return hovered.rowIndex === marker.rowIndex
            && hovered.day === marker.day
            && hovered.accrualNumber === marker.accrualNumber;
    }

    public hoveredTooltipForRow(rowIndex: number): TimelineMarker | null {
        const hovered = this.hoveredMarker();
        if (!hovered || hovered.rowIndex !== rowIndex) return null;
        return hovered;
    }

    public markerXPercent(marker: TimelineMarker): number {
        const total = this.totalVisibleDays();
        if (total <= 0) return 0;
        return (marker.day / total) * 100;
    }

    public tooltipXPercent(marker: TimelineMarker): number {
        return this.markerXPercent(marker);
    }

    public dayToX(day: number): number {
        const total = this.totalVisibleDays();
        if (total <= 0) return 0;
        return (day / total) * VIEWBOX_WIDTH;
    }

    public graceWidth(row: TierRow): number {
        if (row.gracePeriodDays <= 0) return 0;
        return Math.max(0, this.dayToX(row.graceEndDay) - this.dayToX(row.graceStartDay));
    }

    public graceX(row: TierRow): number {
        return this.dayToX(row.graceStartDay);
    }

    public accrualBandX(row: TierRow): number {
        const center = this.dayToX(row.accrualStartDay);
        if (row.occurrenceType === 'Once') {
            return center - this.dayToX(ZERO_WIDTH_BAND_DAYS) / 2;
        }
        return center;
    }

    public accrualBandWidth(row: TierRow): number {
        if (row.occurrenceType === 'Once') {
            return this.dayToX(ZERO_WIDTH_BAND_DAYS);
        }
        if (row.indefinite) {
            return Math.max(0, this.dayToX(this.totalVisibleDays()) - this.dayToX(row.accrualStartDay));
        }
        return Math.max(0, this.dayToX(row.accrualEndDay) - this.dayToX(row.accrualStartDay));
    }

    public accrualSolidWidth(row: TierRow): number {
        return Math.max(0, this.dayToX(row.accrualEndDay) - this.dayToX(row.accrualStartDay));
    }

    public formatDate(date: Date | undefined): string {
        return date ? DateUtilities.format(date, DateFormat.USShort) : '';
    }

    public formatCurrency(amount: number | undefined): string {
        return CurrencyUtilities.convertMinorUnitToDisplayFormat(amount ?? 0, this.userSessionService.currentCurrency());
    }

    public trackRow(_index: number, row: TierRow): number {
        return row.rowIndex;
    }

    public trackMarker(_index: number, marker: TimelineMarker): string {
        return `${marker.rowIndex}-${marker.accrualNumber}-${marker.day}`;
    }

    public occurrenceLabel(row: TierRow): string {
        switch (row.occurrenceType) {
            case 'Once': return 'once';
            case 'Multiple': {
                const freq = row.frequencyDays;
                const count = row.multipleCount;
                if (!freq || freq <= 0 || !count || count <= 0) return 'configure recurrence';
                return `${count}× every ${freq} day${freq === 1 ? '' : 's'}`;
            }
            case 'UntilPaid': {
                const freq = row.frequencyDays;
                if (!freq || freq <= 0) return 'until paid (configure frequency)';
                return `every ${freq} day${freq === 1 ? '' : 's'}, until paid`;
            }
        }
    }

    private buildRow(tier: LateFeeTierUiModel, index: number, dueDate: Date | undefined): TierRow {
        const colorIndex = (index % 4) + 1;
        const grace = Math.max(0, Math.floor(tier.gracePeriodDays || 0));
        const graceStartDay = 1;
        const graceEndDay = grace + 1;
        const accrualStartDay = graceEndDay;

        let accrualEndDay = accrualStartDay;
        let fadeEndDay: number | undefined;
        let indefinite = false;
        let tickDays: number[] = [];
        let accrualCount: number | undefined;
        let tierTotal: number | undefined;

        switch (tier.occurrenceType) {
            case 'Once':
                accrualEndDay = accrualStartDay;
                tickDays = [accrualStartDay];
                accrualCount = 1;
                tierTotal = tier.amount;
                break;

            case 'Multiple': {
                const count = Math.max(1, Math.floor(tier.multipleCount ?? 0));
                const freq = Math.max(1, Math.floor(tier.frequencyDays ?? 0));
                if (count >= 1 && freq >= 1) {
                    for (let k = 0; k < Math.min(count, MAX_TICKS_PER_TIER); k++) {
                        tickDays.push(accrualStartDay + k * freq);
                    }
                    accrualEndDay = accrualStartDay + (count - 1) * freq;
                    accrualCount = count;
                    tierTotal = tier.amount * count;
                }
                break;
            }

            case 'UntilPaid': {
                const freq = Math.max(1, Math.floor(tier.frequencyDays ?? 0));
                indefinite = true;
                fadeEndDay = accrualStartDay + INDEFINITE_VISUAL_TAIL_DAYS;
                if (freq >= 1) {
                    const maxTicks = Math.min(MAX_TICKS_PER_TIER, Math.floor(INDEFINITE_VISUAL_TAIL_DAYS / freq) + 1);
                    for (let k = 0; k < maxTicks; k++) {
                        tickDays.push(accrualStartDay + k * freq);
                    }
                    accrualEndDay = accrualStartDay + (maxTicks - 1) * freq;
                }
                break;
            }
        }

        return {
            rowIndex: index,
            colorIndex,
            amount: tier.amount,
            gracePeriodDays: grace,
            occurrenceType: tier.occurrenceType,
            frequencyDays: tier.frequencyDays,
            multipleCount: tier.multipleCount,
            graceStartDay,
            graceEndDay,
            accrualStartDay,
            accrualEndDay,
            fadeEndDay,
            indefinite,
            tickDays,
            accrualCount,
            tierTotal,
            startDate: dueDate ? DateUtilities.addDays(dueDate, accrualStartDay) : undefined,
            endDate: dueDate && !indefinite ? DateUtilities.addDays(dueDate, accrualEndDay) : undefined,
        };
    }
}
